// session-expiry — central bridge for "any API call just returned 401".
//
// Wiring:
//   1. `apiFetch` (lib/api-client.ts) calls `dispatchSessionExpired()`
//      whenever a non-auth request comes back with HTTP 401. The set
//      of "auth endpoints we deliberately allow to 401 silently"
//      (login, /me probe, refresh) is whitelisted there so a 401 on
//      `/v1/auth/me` of a logged-out browser doesn't bounce the user.
//
//   2. `<SessionExpiryWatcher>` (mounted at SPA root in main.tsx)
//      listens for the event and routes to /login when the user is
//      currently inside a dashboard route. LIFF / scan / public
//      pages handle their own auth; we don't redirect those.
//
//   3. `DashboardSessionProvider` also fires this on
//      `visibilitychange` if `/v1/auth/me` says we're no longer
//      signed in. Catches the "left tab open overnight" case before
//      the user clicks anything.
//
// Throttling: once one expiry is in flight (handler set redirecting
// flag) further 401s are no-ops so a wave of parallel failed requests
// doesn't fire a wave of redirects.

const EVENT_NAME = "location:session-expired";

let redirectInFlight = false;

/**
 * Fire the session-expired event. apiFetch + visibilitychange-watcher
 * both call this. Idempotent for a given browsing session — once a
 * handler kicks off the navigation, subsequent calls are dropped.
 */
export function dispatchSessionExpired(): void {
  if (typeof window === "undefined") return;
  if (redirectInFlight) return;
  redirectInFlight = true;
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

/**
 * Subscribe to the session-expired event. Returns an unsubscribe
 * function. Used by SessionExpiryWatcher; not for general code.
 */
export function onSessionExpired(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const wrap = (): void => handler();
  window.addEventListener(EVENT_NAME, wrap);
  return () => window.removeEventListener(EVENT_NAME, wrap);
}

/**
 * Reset the in-flight flag. Called by SessionExpiryWatcher after a
 * navigation completes (or fails) so a future expiry isn't silently
 * swallowed. In practice the user lands on /login and the SPA
 * re-mounts so the module state is fresh anyway, but this keeps
 * tests / non-redirect handlers correct.
 */
export function resetSessionExpiredFlag(): void {
  redirectInFlight = false;
}
